import logger from "@utils/logger";
import { AwsRum, type AwsRumConfig } from "aws-rum-web";

const identityPoolId = import.meta.env.VITE_AWS_RUM_IDENTITY_POOL_ID;
const endpoint = import.meta.env.VITE_AWS_RUM_ENDPOINT;
const applicationId = import.meta.env.VITE_AWS_RUM_APPLICATION_ID;
const applicationVersion = import.meta.env.VITE_AWS_RUM_APPLICATION_VERSION;
const applicationRegion = import.meta.env.VITE_AWS_RUM_APPLICATION_REGION;

// AWS default sample rates:
// https://docs.aws.amazon.com/sdk-for-kotlin/api/latest/rum/aws.sdk.kotlin.services.rum.model/-app-monitor-configuration/session-sample-rate.html
const SESSION_SAMPLE_RATE = 0.1;

export class AwsRumInstance {
  private instance: AwsRum | null = null;

  constructor() {
    if (!this.isAvailable()) {
      logger._console.warn("[AWS RUM] Not available to initialize");
      return;
    }

    this.init();
  }

  /**
   * Initialize the AWS RUM instance
   */
  private init(): void {
    try {
      const config: AwsRumConfig = {
        sessionSampleRate: SESSION_SAMPLE_RATE,
        identityPoolId: identityPoolId,
        endpoint: endpoint,
        telemetries: ["performance", "errors", "http"],
        allowCookies: true,
        enableXRay: false,
        signing: true,
      };

      this.instance = new AwsRum(applicationId!, applicationVersion!, applicationRegion!, config);
    } catch (error) {
      logger._console.error("[AWS RUM] Failed to initialize", error);
    }
  }

  /**
   * Check if AWS RUM is available to initialize
   */
  private isAvailable(): boolean {
    return !!(identityPoolId && endpoint && applicationId && applicationVersion && applicationRegion);
  }

  /**
   * Get the AWS RUM instance
   */
  public getInstance(): AwsRum | null {
    return this.instance;
  }

  /**
   * Record an error to AWS RUM
   */
  public recordError(error: any): void {
    this.instance?.recordError(error);
  }
}

// Create and export a singleton instance
export const awsRum = new AwsRumInstance();

export const initAWSRUM = () => {
  return awsRum;
};
